import React, { useState, useEffect, useContext } from "react";
import { FaUsers, FaBox, FaShoppingBag, FaMoneyCheckAlt } from "react-icons/fa";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';

import { UserContext } from '../App';

// Custom hook to handle data fetching logic
const useFetchDashboardData = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const API_URL = process.env.REACT_APP_API_URL;
        const accessToken = localStorage.getItem("accessToken");
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
        };

        const fetchData = async () => {
            console.log("Tentative de récupération des données du tableau de bord...");
            try {
                const res = await fetch(`${API_URL}/dashboard-analytics`, { headers });

                if (!res.ok) {
                    console.error("Réponse API non OK:", res.status, res.statusText);
                    throw new Error("Échec de la récupération des données analytiques du tableau de bord.");
                }

                // La base de données renvoie un objet directement, pas un tableau.
                const dashboardData = await res.json();

                console.log("Données brutes de l'API:", dashboardData);

                if (!dashboardData) {
                    console.error("L'objet de données est vide ou non défini.");
                    setData(null);
                    return;
                }

                // Les données sont déjà au format objet JavaScript grâce au backend.
                // Le `JSON.parse` n'est donc pas nécessaire.
                const formattedData = {
                    ...dashboardData,
                    monthlyRevenue: dashboardData.monthlyRevenue?.map(item => ({ name: item.month, "Revenus ($)": item.revenue })),
                    userGrowth: dashboardData.userGrowth?.map(item => ({ name: item.month, "Nouveaux Utilisateurs": item.count })),
                    topProducts: dashboardData.topProducts || [],
                    revenueByCategory: Object.keys(dashboardData.revenueByCategory || {}).map(key => ({
                        name: key,
                        value: dashboardData.revenueByCategory[key]
                    }))
                };

                console.log("Données parsées et formatées avec succès:", formattedData);
                setData(formattedData);

            } catch (err) {
                console.error("Erreur API:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    return { data, loading, error };
};

// Reusable Metric Card with a new professional design
const MetricCard = ({ title, value, icon: Icon, color }) => (
    <div className={`p-6 bg-white bg-opacity-80 backdrop-filter backdrop-blur-lg rounded-xl shadow-lg transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl border-l-4 ${color}`}>
        <div className="flex items-center justify-between">
            <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase">{title}</h3>
                <p className="mt-1 text-4xl font-extrabold text-gray-900">{value}</p>
            </div>
            <div className={`p-4 rounded-full bg-opacity-20 ${color.replace('border-l-', 'bg-')}`}>
                <Icon className={`text-3xl ${color.replace('border-l-', 'text-')}`} />
            </div>
        </div>
    </div>
);

// Component for the Top Products Table
const TopProductTable = ({ products }) => (
    <div className="bg-white bg-opacity-80 backdrop-filter backdrop-blur-lg p-6 rounded-xl shadow-lg h-full">
        <h3 className="text-lg font-bold text-gray-800 mb-4">5 produits les plus vendus</h3>
        <ul className="divide-y divide-gray-200">
            {products.length > 0 ? (
                products.map((product, index) => (
                    <li key={index} className="py-3 flex items-center justify-between">
                        <span className="text-gray-700 font-medium truncate">{product.product_name}</span>
                        <span className="text-gray-500 text-sm">{product.sales_count} ventes</span>
                    </li>
                ))
            ) : (
                <li className="py-3 text-center text-gray-500">Aucune donnée de vente.</li>
            )}
        </ul>
    </div>
);

const CHART_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#3b82f6', '#14b8a6'];

// Main Dashboard component
const DashboardPage = () => {
    const { data, loading, error } = useFetchDashboardData();
    const { user } = useContext(UserContext);
    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-100">
                <div className="text-2xl text-gray-700 animate-pulse">Chargement des données du tableau de bord...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-100">
                <div className="p-8 bg-white border border-red-400 text-red-700 rounded-lg shadow-md">
                    <p className="font-semibold text-lg">Erreur:</p>
                    <p className="mt-2">{error}</p>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-100">
                <div className="p-8 bg-white rounded-lg shadow-md text-gray-700 text-center">
                    <h2 className="text-2xl font-bold">Aucune donnée disponible</h2>
                    <p className="mt-2">Veuillez vérifier vos requêtes et les données de votre base de données.</p>
                </div>
            </div>
        );
    }

    // Convert totalRevenue to a number before rendering to avoid the toFixed error
    const totalRevenueValue = parseFloat(data.totalRevenue) || 0;
    const adminName = user?.lastName ? `${user.firstName} ${user.lastName}` : 'Livreur';
    return (
        <div className="min-h-screen bg-gray-100 font-sans p-8 text-gray-900">
            <div className="max-w-8xl mx-auto">
                <header className="mb-8">
                    <h1 className="text-4xl font-extrabold text-gray-900">
                        Bienvenue sur le tableau de bord! 🚀
                    </h1>
                    <p className="mt-2 text-lg text-gray-500">
                        Bienvenue sur votre tableau de bord, <span className="text-indigo-400">{adminName}</span>! Votre aperçu des performances de votre plateforme.
                    </p>
                </header>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <MetricCard
                        title="Utilisateurs Totaux"
                        value={data.totalUsers?.toLocaleString() || 'N/A'}
                        icon={FaUsers}
                        color="border-indigo-500"
                    />
                    <MetricCard
                        title="Produits Totaux"
                        value={data.totalProducts?.toLocaleString() || 'N/A'}
                        icon={FaBox}
                        color="border-green-500"
                    />
                    <MetricCard
                        title="Commandes Totales"
                        value={data.totalOrders?.toLocaleString() || 'N/A'}
                        icon={FaShoppingBag}
                        color="border-yellow-500"
                    />
                    <MetricCard
                        title="Revenu Total"
                        value={`CDF ${totalRevenueValue.toFixed(2).toLocaleString()}`}
                        icon={FaMoneyCheckAlt}
                        color="border-red-500"
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                    <div className="lg:col-span-2 bg-white bg-opacity-80 backdrop-filter backdrop-blur-lg p-6 rounded-xl shadow-lg h-[450px]">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">Revenus Mensuels</h3>
                        <ResponsiveContainer width="100%" height="85%">
                            <LineChart data={data.monthlyRevenue}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="name" tickLine={false} axisLine={false} />
                                <YAxis tickLine={false} axisLine={false} />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="Revenus ($)" stroke="#6366f1" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 8 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="bg-white bg-opacity-80 backdrop-filter backdrop-blur-lg p-6 rounded-xl shadow-lg h-[450px]">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">Revenus par Catégorie</h3>
                        <ResponsiveContainer width="100%" height="85%">
                            <PieChart>
                                <Pie
                                    data={data.revenueByCategory}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={120}
                                    label
                                >
                                    {data.revenueByCategory.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 bg-white bg-opacity-80 backdrop-filter backdrop-blur-lg p-6 rounded-xl shadow-lg h-[450px]">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">Croissance des Utilisateurs</h3>
                        <ResponsiveContainer width="100%" height="85%">
                            <BarChart data={data.userGrowth}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="name" tickLine={false} axisLine={false} />
                                <YAxis tickLine={false} axisLine={false} />
                                <Tooltip />
                                <Bar dataKey="Nouveaux Utilisateurs" fill="#10b981" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <TopProductTable products={data.topProducts || []} />
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;
